import type { ComponentType } from 'react';
import type { Language, TodayView, WeekView } from '../types';

/**
 * The household dashboard is assembled from modules rather than hard-coded.
 *
 * Meals are the first thing this household needs, but the same page is the
 * natural home for the rest of running a family — who is collecting the
 * children, this week's clubs and activities, whose turn it is to cook.
 *
 * To add one:
 *   1. write a component that takes `DashboardModuleProps`
 *   2. add an entry to DASHBOARD_MODULES below
 *
 * Nothing else in the app needs to change. Give it an `order` to place it, and
 * an `enabled` predicate if it should only appear in some situations.
 */
export interface DashboardModuleProps {
  /** Today's snapshot, refreshed by the Week page. */
  today: TodayView | null;
  /** The week currently being viewed. */
  week: WeekView | null;
  language: Language;
  /** Ask the page to reload its data, e.g. after an action inside a module. */
  refresh: () => void;
  /** Whether the viewer is a shared device rather than a signed-in adult (§7). */
  isDevice: boolean;
}

/**
 * Which page a module belongs to.
 *
 * "today" is the landing page and answers what is happening now — tonight's
 * meal, who is collecting the children, the weather. "plan" is the sit-down
 * job of filling in the week up to the next shop. Mixing the two made both
 * harder to read.
 */
export type ModulePage = 'today' | 'plan';

export interface DashboardModule {
  id: string;
  page: ModulePage;
  /** Lower numbers appear first. */
  order: number;
  component: ComponentType<DashboardModuleProps>;
  /** Hide the module entirely when this returns false. */
  enabled?: (props: DashboardModuleProps) => boolean;
  /** Set to true for modules that should also appear on the kitchen tablet view. */
  showOnKitchen?: boolean;
}

import { TodayModule } from './TodayModule';
import { WeatherModule } from './WeatherModule';
import { WeekModule } from './WeekModule';
import { WeekSetupModule } from './WeekSetupModule';
import { SchoolRunModule } from './SchoolRunModule';
import { HorizonModule } from './HorizonModule';

export const DASHBOARD_MODULES: DashboardModule[] = [
  { id: 'today', page: 'today', order: 10, component: TodayModule, showOnKitchen: true },
  { id: 'school-run', page: 'today', order: 15, component: SchoolRunModule, showOnKitchen: true },
  {
    id: 'weather',
    page: 'today',
    order: 20,
    component: WeatherModule,
    showOnKitchen: true,
    enabled: ({ today }) => Boolean(today?.weather && !today.weather.unavailable),
  },

  { id: 'horizon', page: 'plan', order: 10, component: HorizonModule },
  { id: 'week', page: 'plan', order: 20, component: WeekModule },
  // Configuration for the plan, so it sits below the plan.
  { id: 'week-setup', page: 'plan', order: 30, component: WeekSetupModule },

  // Future modules slot in here, for example:
  // { id: 'activities', page: 'today', order: 18, component: ActivitiesModule, showOnKitchen: true },
];

export function visibleModules(
  page: ModulePage,
  props: DashboardModuleProps,
  filter?: (module: DashboardModule) => boolean,
): DashboardModule[] {
  return DASHBOARD_MODULES.filter((module) => module.page === page)
    .filter((module) => (filter ? filter(module) : true))
    .filter((module) => (module.enabled ? module.enabled(props) : true))
    .sort((a, b) => a.order - b.order);
}
